import pako from 'pako';
import { useFile, useVisualizer } from '../../context/AppContext.jsx';
import { useExport } from '../../hooks/useExport.js';
import { useRobotConnection } from '../../hooks/useRobotConnection.js';

export default function ExportSection() {
  const { currentFileData, currentInstallationData, parser, currentFileName } = useFile();
  const { isServerAvailable } = useVisualizer();
  const { exportURP, exportInstallation } = useExport();
  const { uploadToRobotFTP } = useRobotConnection();

  const handleUploadToRobot = async () => {
    if (!currentFileData) {
      return;
    }

    try {
      // Get the XML string, encode it, and compress with gzip
      const xmlString = parser.getXML();
      const encoder = new TextEncoder();
      const xmlBytes = encoder.encode(xmlString);
      const compressed = pako.gzip(xmlBytes);
      const fileName = currentFileName || 'program.urp';

      await uploadToRobotFTP(fileName, compressed);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  if (!currentFileData) return null;

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-border flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        </svg>
        <span>Export</span>
      </h2>
      <div className="space-y-2">
        {isServerAvailable && (
          <button
            onClick={handleUploadToRobot}
            className="w-full bg-success text-white px-3 py-1.5 text-sm rounded hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 17.647 17.647" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <g>
                <g>
                  <circle cx="2.648" cy="7.546" r="1.11"></circle>
                  <path d="M5.296,7.546c0-1.46-1.188-2.648-2.648-2.648C1.189,4.898,0,6.086,0,7.546s1.188,2.648,2.648,2.648 C4.108,10.194,5.296,9.006,5.296,7.546z M2.648,9.567c-1.114,0-2.021-0.906-2.021-2.021s0.907-2.021,2.021-2.021 c1.115,0,2.021,0.906,2.021,2.021S3.762,9.567,2.648,9.567z"></path>
                  <path d="M5.655,9.221C5.527,9.576,5.324,9.91,5.041,10.194c-0.306,0.305-0.668,0.516-1.052,0.641 l2.896,2.898h-0.56v2.035h5.435v-2.035h-1.596L5.655,9.221z"></path>
                  <path d="M17.572,4.686c-0.104-0.104-0.271-0.107-0.376-0.004l-2.418,2.364l-1.78-0.934l-0.467-2.034 l1.192-1.618l3.206,1.073c0.049,0.016,0.099,0.018,0.146,0.007c0.087-0.02,0.162-0.083,0.191-0.175 c0.049-0.139-0.027-0.291-0.166-0.337l-3.391-1.135c-0.11-0.038-0.23,0.001-0.298,0.094l-1.085,1.47l-0.895,0.205l0.126,0.548 L5.294,5.648c0.232,0.297,0.407,0.646,0.497,1.038c0.096,0.42,0.085,0.839-0.014,1.232l6.298-1.447l0.126,0.548L13.154,6.8 l-0.002-0.004L14.7,7.609c0.059,0.03,0.124,0.038,0.184,0.023c0.047-0.011,0.092-0.034,0.126-0.069l2.556-2.501 C17.672,4.96,17.674,4.791,17.572,4.686z"></path>
                </g>
              </g>
            </svg>
            Upload to Robot
          </button>
        )}
        <button 
          onClick={exportURP}
          className="w-full bg-success text-white px-3 py-1.5 text-sm rounded hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export .urp
        </button>
        {currentInstallationData && (
          <button 
            onClick={exportInstallation}
            className="w-full bg-success text-white px-3 py-1.5 text-sm rounded hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export .installation
          </button>
        )}
      </div>
      <p className="text-text-secondary text-sm text-center mt-2">Export your modified program and installation files</p>
    </section>
  );
}

